/**
 * HeroDiagram — fine-line governance diagram for the Cierge Labs hero.
 *
 * Three CAR primitives (Custody · Authority · Receipts) wired into a
 * KATLAS-signed receipt artefact. KATLAS blue for governance lines,
 * single amber accent for the receipt only. No stock imagery, no
 * crypto/Web3 tropes, no robot iconography.
 */
export const HeroDiagram = () => {
  return (
    <div
      data-testid="hero-diagram"
      className="relative h-full w-full overflow-hidden border border-white/10"
      style={{
        background:
          "radial-gradient(120% 80% at 50% 0%, #0F3A66 0%, #0A2540 55%, #061A2F 100%)",
      }}
    >
      {/* subtle grid texture */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.18]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />

      {/* CAR + receipt SVG */}
      <svg
        viewBox="0 0 520 640"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute inset-0 h-full w-full"
        role="img"
        aria-label="Custody, Authority, Receipts — wired into a KATLAS-signed receipt"
      >
        <defs>
          <linearGradient id="cl-line" x1="0" x2="1">
            <stop offset="0%" stopColor="#7FB2FF" stopOpacity="0.15" />
            <stop offset="50%" stopColor="#7FB2FF" stopOpacity="0.65" />
            <stop offset="100%" stopColor="#7FB2FF" stopOpacity="0.15" />
          </linearGradient>
          <radialGradient id="cl-node-glow" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="#3D7FE6" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#3D7FE6" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* connecting lines */}
        <g stroke="url(#cl-line)" strokeWidth="1" fill="none">
          <path d="M 110 150 L 260 100 L 410 150" />
          <path d="M 110 150 L 260 360" />
          <path d="M 260 100 L 260 360" strokeDasharray="2 4" />
          <path d="M 410 150 L 260 360" />
          <path d="M 260 360 L 260 460" />
        </g>

        {/* nodes — Custody (C), Authority (A), Receipts (R) */}
        {[
          { cx: 110, cy: 150, label: "C", caption: "Custody" },
          { cx: 260, cy: 100, label: "A", caption: "Authority" },
          { cx: 410, cy: 150, label: "R", caption: "Receipts" },
        ].map((n) => (
          <g key={n.label}>
            <circle cx={n.cx} cy={n.cy} r="44" fill="url(#cl-node-glow)" />
            <circle
              cx={n.cx}
              cy={n.cy}
              r="26"
              fill="#0A2540"
              stroke="#7FB2FF"
              strokeWidth="1"
            />
            <text
              x={n.cx}
              y={n.cy + 6}
              textAnchor="middle"
              fontFamily="IBM Plex Mono, monospace"
              fontSize="18"
              fill="#E6F0FF"
              fontWeight="500"
            >
              {n.label}
            </text>
            <text
              x={n.cx}
              y={n.cy + 56}
              textAnchor="middle"
              fontFamily="IBM Plex Mono, monospace"
              fontSize="9"
              letterSpacing="2"
              fill="#7FB2FF"
              opacity="0.85"
            >
              {n.caption.toUpperCase()}
            </text>
          </g>
        ))}

        {/* KATLAS plane marker */}
        <g>
          <circle cx="260" cy="360" r="34" fill="#0A2540" stroke="#7FB2FF" strokeWidth="1" />
          <text
            x="260"
            y="358"
            textAnchor="middle"
            fontFamily="Cormorant Garamond, serif"
            fontSize="18"
            fill="#E6F0FF"
            fontStyle="italic"
          >
            katlas
          </text>
          <text
            x="260"
            y="372"
            textAnchor="middle"
            fontFamily="IBM Plex Mono, monospace"
            fontSize="7"
            letterSpacing="2"
            fill="#7FB2FF"
          >
            NODE
          </text>
        </g>

        {/* Signed receipt artefact (amber-accented) */}
        <g>
          <rect
            x="100"
            y="460"
            width="320"
            height="120"
            rx="0"
            fill="#0F1E2D"
            stroke="#C19A4A"
            strokeWidth="1"
          />
          <text
            x="120"
            y="486"
            fontFamily="IBM Plex Mono, monospace"
            fontSize="9"
            letterSpacing="2"
            fill="#C19A4A"
          >
            RECEIPT · 0x9A4F…C12D
          </text>
          <text
            x="400"
            y="486"
            textAnchor="end"
            fontFamily="IBM Plex Mono, monospace"
            fontSize="9"
            letterSpacing="2"
            fill="#C19A4A"
          >
            VERIFIED
          </text>
          <line x1="120" y1="496" x2="400" y2="496" stroke="#C19A4A" strokeOpacity="0.25" />
          {[
            ["action", "sponsor.introduction.approve"],
            ["custody", "member:cc.m_0419"],
            ["authority", "role:club_secretary"],
            ["shared / withheld", "3 refs · 5 withheld"],
          ].map(([k, v], i) => (
            <g key={k}>
              <text
                x="120"
                y={514 + i * 16}
                fontFamily="IBM Plex Mono, monospace"
                fontSize="9"
                letterSpacing="1"
                fill="#7FB2FF"
                opacity="0.9"
              >
                {k}
              </text>
              <text
                x="400"
                y={514 + i * 16}
                textAnchor="end"
                fontFamily="IBM Plex Mono, monospace"
                fontSize="9"
                fill="#E6F0FF"
              >
                {v}
              </text>
            </g>
          ))}
        </g>
      </svg>

      {/* footer caption */}
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between border-t border-white/10 bg-black/30 px-5 py-3 font-mono text-[9px] uppercase tracking-[0.22em] text-white/60 backdrop-blur-sm">
        <span>Reference · governed action pathway</span>
        <span>Illustrative · not a live receipt</span>
      </div>
    </div>
  );
};

export default HeroDiagram;
